/*
 * Omnitub AI — on-device product translation via Chrome's built-in Translator API.
 *
 * Runs entirely client-side in Chrome 138+. No server calls. No PII leaves the browser.
 * Silent no-op on unsupported browsers (iOS Safari, older Chrome, mobile Chrome on iOS).
 *
 * Targets elements with the class `js-omnitub-translate` (added in main-product.liquid).
 * Source language is always English (UK store).
 */
(function () {
  'use strict';

  // Native-script labels — visitors recognise their own language without us labelling it for them.
  var SUPPORTED = {
    fr: 'Français',
    de: 'Deutsch',
    es: 'Español',
    it: 'Italiano',
    nl: 'Nederlands',
    pt: 'Português',
    ja: '日本語',
    'zh-Hant': '中文 (繁體)',
    'zh-Hans': '中文 (简体)',
    ko: '한국어',
    ar: 'العربية',
    pl: 'Polski',
    sv: 'Svenska',
    da: 'Dansk',
    no: 'Norsk',
    fi: 'Suomi',
    ru: 'Русский',
    tr: 'Türkçe',
    el: 'Ελληνικά',
    cs: 'Čeština',
    hu: 'Magyar',
    ro: 'Română',
    he: 'עברית',
    th: 'ไทย',
    vi: 'Tiếng Việt',
    hi: 'हिन्दी',
    id: 'Bahasa Indonesia',
    uk: 'Українська'
  };

  var STORAGE_KEY = 'omnitub_lang_pref';
  var SOURCE = 'en';
  var originals = new WeakMap();

  function pickTarget() {
    // URL override for testing/demos: ?omnitub_lang=fr
    try {
      var override = new URLSearchParams(location.search).get('omnitub_lang');
      if (override && SUPPORTED[override]) return override;
    } catch (e) {}
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED[saved]) return saved;
    } catch (e) {}
    var langs = navigator.languages || [navigator.language || 'en-GB'];
    for (var i = 0; i < langs.length; i++) {
      var raw = langs[i];
      if (!raw) continue;
      var lower = raw.toLowerCase();
      if (lower.indexOf('en') === 0) continue;
      if (SUPPORTED[raw]) return raw;
      var base = lower.split('-')[0];
      if (SUPPORTED[base]) return base;
      // Chinese region mapping
      if (base === 'zh') {
        if (/zh-(tw|hk|mo|hant)/.test(lower)) return 'zh-Hant';
        return 'zh-Hans';
      }
    }
    return null;
  }

  async function isAvailable(target) {
    if (!('Translator' in self)) return false;
    try {
      var status = await self.Translator.availability({
        sourceLanguage: SOURCE,
        targetLanguage: target
      });
      return status && status !== 'unavailable';
    } catch (e) {
      return false;
    }
  }

  function buildPill(target) {
    var label = SUPPORTED[target];
    var wrap = document.createElement('div');
    wrap.className = 'omnitub-ai__pill-wrap';
    wrap.innerHTML =
      '<button class="omnitub-ai__pill" data-target="' + target + '" type="button" aria-live="polite">' +
        '<span class="omnitub-ai__pill-label">Read in ' + label + '</span>' +
      '</button>' +
      '<div class="omnitub-ai__status" hidden></div>';
    return wrap;
  }

  function buildFooter() {
    var f = document.createElement('div');
    f.className = 'omnitub-ai__footer';
    f.innerHTML =
      '<span class="omnitub-ai__footer-text">' +
        'Translated on your device by Chrome — nothing sent to Omnitub. ' +
        'For specifications, the English original is the source of truth.' +
      '</span>' +
      '<button type="button" class="omnitub-ai__restore" aria-label="Restore English">Restore English ↩</button>';
    return f;
  }

  function collectTextNodes(root) {
    var nodes = [];
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        var txt = node.nodeValue;
        if (!txt || !txt.trim()) return NodeFilter.FILTER_REJECT;
        var p = node.parentElement;
        if (p && /^(SCRIPT|STYLE|CODE|PRE)$/i.test(p.tagName)) return NodeFilter.FILTER_REJECT;
        if (p && p.closest('.omnitub-ai__footer')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var n;
    while ((n = walker.nextNode())) nodes.push(n);
    return nodes;
  }

  async function translateBlock(block, translator, statusEl) {
    var nodes = collectTextNodes(block);
    statusEl.hidden = false;
    statusEl.textContent = 'Translating';

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (!originals.has(node)) originals.set(node, node.nodeValue);
      var src = originals.get(node);
      // Don't waste a call on numeric-only / very short tokens
      if (!/[A-Za-z]{2,}/.test(src)) continue;
      try {
        node.nodeValue = await translator.translate(src);
      } catch (e) {
        // leave node alone, move on
      }
      if ((i + 1) % 4 === 0 || i === nodes.length - 1) {
        statusEl.textContent = 'Translating — ' + Math.min(i + 1, nodes.length) + '/' + nodes.length;
      }
    }
    statusEl.hidden = true;
  }

  function restoreEnglish(block) {
    var nodes = collectTextNodes(block);
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (originals.has(node)) node.nodeValue = originals.get(node);
    }
  }

  // ONE pill at the top of the product info translates EVERY .js-omnitub-translate
  // block on the page (description + spec sheet + delivery info + custom tabs).
  // Auto-opens any closed <details> disclosure containing one of the blocks so
  // the translation is visible in place.
  async function setup(blocks, target) {
    var label = SUPPORTED[target];

    var pillWrap = buildPill(target);
    var firstBlock = blocks[0];
    var insertBefore = firstBlock.closest('details-disclosure') || firstBlock.closest('details') || firstBlock;
    insertBefore.parentNode.insertBefore(pillWrap, insertBefore);

    var pill = pillWrap.querySelector('.omnitub-ai__pill');
    var pillLabel = pillWrap.querySelector('.omnitub-ai__pill-label');
    var statusEl = pillWrap.querySelector('.omnitub-ai__status');
    var footer = null;

    function disclosuresFor(blocks) {
      var ds = [];
      for (var i = 0; i < blocks.length; i++) {
        var d = blocks[i].closest('details');
        if (d && ds.indexOf(d) === -1) ds.push(d);
      }
      return ds;
    }

    async function activate() {
      if (pill.classList.contains('omnitub-ai__pill--active')) return;
      // Open every disclosure that contains one of our blocks so translations are visible.
      disclosuresFor(blocks).forEach(function (d) { if (!d.open) d.open = true; });
      pill.disabled = true;
      pill.classList.add('omnitub-ai__pill--working');
      statusEl.hidden = false;
      statusEl.textContent = 'Preparing on your device — first time may take a moment.';
      try {
        var translator = await self.Translator.create({
          sourceLanguage: SOURCE,
          targetLanguage: target,
          monitor: function (m) {
            m.addEventListener('downloadprogress', function (e) {
              statusEl.textContent = 'Downloading translation model — ' + Math.round(e.loaded * 100) + '%';
            });
          }
        });
        // Translate every hooked block in turn. We share the translator instance
        // so the ~22MB language pack download only happens once.
        for (var i = 0; i < blocks.length; i++) {
          if (blocks.length > 1) {
            statusEl.textContent = 'Translating section ' + (i + 1) + ' of ' + blocks.length;
          }
          await translateBlock(blocks[i], translator, statusEl);
        }
        try { localStorage.setItem(STORAGE_KEY, target); } catch (e) {}
        // Footer attached to the LAST block — visually closes the translated region.
        footer = buildFooter();
        blocks[blocks.length - 1].appendChild(footer);
        footer.querySelector('.omnitub-ai__restore').addEventListener('click', deactivate);
        pillLabel.textContent = 'Showing in ' + label;
        pill.classList.remove('omnitub-ai__pill--working');
        pill.classList.add('omnitub-ai__pill--active');
        pill.disabled = false;
      } catch (e) {
        console.warn('[omnitub-ai] translation failed', e);
        statusEl.hidden = false;
        statusEl.textContent = 'Couldn’t translate. The English original remains.';
        pill.classList.remove('omnitub-ai__pill--working');
        pill.disabled = false;
      }
    }

    function deactivate() {
      for (var i = 0; i < blocks.length; i++) restoreEnglish(blocks[i]);
      if (footer) { footer.remove(); footer = null; }
      pill.classList.remove('omnitub-ai__pill--active');
      pillLabel.textContent = 'Read in ' + label;
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    }

    pill.addEventListener('click', function () {
      if (pill.classList.contains('omnitub-ai__pill--active')) deactivate();
      else activate();
    });

    return { activate: activate, deactivate: deactivate };
  }

  async function init() {
    var target = pickTarget();
    if (!target) return;
    if (!(await isAvailable(target))) return;

    var blocks = Array.prototype.slice.call(document.querySelectorAll('.js-omnitub-translate'));
    if (!blocks.length) return;

    var ctrl = await setup(blocks, target);

    var saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch (e) {}
    if (saved === target && ctrl) ctrl.activate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
